/**
 * OAuth2 PKCE (Proof Key for Code Exchange) utility functions
 * Implements RFC 7636 for secure authorization code flow
 */

/**
 * Base64 URL encoding without padding, according to RFC 4648 §5.
 * Used for PKCE code_challenge generation.
 */
function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * SHA-256 hash using SubtleCrypto API.
 * Used to generate PKCE code_challenge from code_verifier.
 */
async function sha256(input: string): Promise<ArrayBuffer> {
  const data = new TextEncoder().encode(input);
  return await crypto.subtle.digest('SHA-256', data);
}

/**
 * Generates a cryptographically random code_verifier string (43-128 characters).
 * This is used in PKCE flow to prevent authorization code interception attacks.
 *
 * @param length - Length of the verifier (default: 64, min: 43, max: 128)
 * @returns Random string suitable for PKCE code_verifier
 */
export const generateCodeVerifier = (length: number = 64): string => {
  if (length < 43 || length > 128) {
    throw new Error(
      'code_verifier length must be between 43 and 128 characters'
    );
  }

  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  return Array.from(randomValues, x => charset[x % charset.length]).join('');
};

/**
 * Generates PKCE code_challenge from code_verifier using SHA-256.
 * The challenge is sent to the authorization server, while verifier is kept secret.
 *
 * @param codeVerifier - The secret code verifier string
 * @returns Base64 URL encoded SHA-256 hash of the verifier
 */
export const generateCodeChallenge = async (
  codeVerifier: string
): Promise<string> => {
  const hash = await sha256(codeVerifier);
  return base64UrlEncode(hash);
};

/**
 * Generates a random state parameter for OAuth2 authorization request.
 * Used to prevent CSRF attacks by validating the response matches the request.
 *
 * @param length - Length of the state string (default: 32)
 * @returns Random state string
 */
export const randomState = (length = 32): string => {
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => charset[b % charset.length]).join('');
};
